import moment from "moment";
import { FC, useMemo } from "react";
import SuccessIcon from "shared/icons/status-icons/success-icon.svg?react";
import SubscribeIcon from "./assets/log-in.svg?react";
import UnsubscribeIcon from "./assets/log-out.svg?react";
import UpdateIcon from "./assets/edit-icon.svg?react";
import { TimeLineItem } from "shared/components/TimeLine";
import {
  AudiLog,
  AuditableType,
  LogChangesType,
  LogEvent,
  UpdateFields,
  UpdateTitles
} from "views/rent-reporting-id/types";
import { ArrowRight } from "untitledui-js-base";

type Props = {
  log: AudiLog;
  isLast?: boolean;
};

const getReadableChanges = (changes: LogChangesType) => {
  return Object.entries(changes).map(([key, values]) => {
    const readableTitle = UpdateTitles[key as UpdateFields] || key;
    return {
      title: readableTitle,
      values
    };
  });
};

export const LogDescription: FC<Props> = ({ log, isLast }) => {
  const logInfo = useMemo(() => {
    if (
      log.event === LogEvent.CREATE &&
      log.auditable_type === AuditableType.BOOM_PARTNERS_CUSTOMER
    )
      return {
        title: log.actor.name
          ? `${log.actor.name} subscribed this renter`
          : "Renter was subscribed",
        icon: <SubscribeIcon className="w-4 h-4 text-success-700" />
      };

    if (log.event === LogEvent.UPDATE) {
      if (log.auditable_type === AuditableType.BOOM_CUSTOMER) {
        const changes = log.changes || {};
        const formattedChanges = getReadableChanges(changes);
        return {
          title: `${log.actor.name || ""} changed `,
          icon: <UpdateIcon className="w-4 h-4 text-content-500" />,
          changes: formattedChanges
        };
      }

      if (
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- that's fine
        log.auditable_type === AuditableType.BOOM_PARTNERS_CUSTOMER &&
        log.changes?.active
      )
        return {
          title: log.actor.name
            ? `${log.actor.name} unsubscribed this renter`
            : "Auto unsubscribed renter based on move out date",
          icon: <UnsubscribeIcon className="w-4 h-4 text-danger-500" />
        };
    }

    if (log.event === LogEvent.MANUAL_VERIFY)
      return {
        title: `${log.actor.name || "Auto"} reported rent`,
        icon: <SuccessIcon className="w-4 h-4 text-success-700" />,
        changes: [
          {
            values: [
              `${moment(log.auditable_entity.for_month).format("MMM YYYY")}`,
              log.auditable_entity.months_delinquency > 0
                ? `${log.auditable_entity.months_delinquency} Month Late`
                : "Paid On-Time"
            ]
          }
        ]
      };

    if (log.event === LogEvent.REQUIRE_MANUAL_VERIFICATION)
      return {
        title: `${log.actor.name || "Auto"} reported rent`,
        icon: <SuccessIcon className="w-4 h-4 text-success-700" />,
        changes: [
          {
            values: [
              `${moment(log.auditable_entity.for_month).format("MMM YYYY")}`,
              "No data"
            ]
          }
        ]
      };

    return null;
  }, [log]);

  if (!logInfo) return null;

  return (
    <TimeLineItem
      wrapperClassName="items-center pt-12 flex"
      isLast={isLast}
      bullet={
        <div className="border border-alpha-100 translate-x-[-16px] p-2 rounded-full bg-background-1000">
          {logInfo.icon}
        </div>
      }
      childrenClassName="flex-1 w-full"
    >
      {logInfo.changes?.length ? (
        <>
          {logInfo.changes.map((change, index) => (
            <div key={index} className="mb-2">
              <p className="text-content-1000 font-medium text-base mb-2">
                {logInfo.title} {change?.title}
              </p>
              <div className="flex gap-4 bg-alpha-40 py-3 px-4 rounded-lg border border-alpha-100 items-center">
                <p className="font-medium text-xs text-content-400">
                  {change.values[0] || "No data"}
                </p>
                <ArrowRight className="text-content-100" size="16" />
                <p className="font-medium text-xs text-content-400">
                  {change.values[1]}
                </p>
              </div>
            </div>
          ))}
          <p className="text-xs text-content-300 font-medium">
            {moment(log.created_at).format("MMM D, YYYY [at] h:mm A")}
          </p>
        </>
      ) : (
        <>
          <p className="text-content-1000 font-medium text-base mb-1">
            {logInfo.title}
          </p>
          <p className="text-xs text-content-300 font-medium">
            {moment(log.created_at).format("MMM D, YYYY [at] h:mm A")}
          </p>
        </>
      )}
    </TimeLineItem>
  );
};
